import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { ShieldLogo } from "../components/landing/Logo";
import { Award, ArrowRight, CheckCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { ChatWidget } from "../components/landing/ChatWidget";
import confetti from "canvas-confetti";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ThankYouPage() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const status = params.get("status");
  const [memberNum, setMemberNum] = useState(params.get("member") || "0");
  const memberName = params.get("name") || "Partner";
  const memberEmail = params.get("email") || "";
  const [headache, setHeadache] = useState("");
  const [pos, setPos] = useState("");
  const [masterclass, setMasterclass] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [paymentConfirmed, setPaymentConfirmed] = useState(false);

  // Check payment status if coming from Stripe
  useEffect(() => {
    if (sessionId && status === "success") {
      setCheckingPayment(true);
      const checkPayment = async () => {
        try {
          const res = await axios.get(`${API}/checkout/status/${sessionId}`);
          if (res.data.payment_status === "paid") {
            setPaymentConfirmed(true);
            toast.success("Payment confirmed! Welcome to the Founding 50!");
            // Fire Gold Confetti celebration
            const duration = 4000;
            const end = Date.now() + duration;
            const colors = ['#D4AF37', '#FFD700', '#FFA500', '#ffffff'];
            (function frame() {
              confetti({ particleCount: 5, angle: 60, spread: 55, origin: { x: 0 }, colors });
              confetti({ particleCount: 5, angle: 120, spread: 55, origin: { x: 1 }, colors });
              if (Date.now() < end) requestAnimationFrame(frame);
            })();
            // Track conversion
            if (typeof window !== 'undefined' && typeof window.gtag !== 'undefined') {
              window.gtag('event', 'purchase', {
                'event_category': 'conversion',
                'event_label': 'founding_member',
                'value': res.data.amount
              });
            }
          }
        } catch (err) {
          console.error("Payment check failed:", err);
        } finally {
          setCheckingPayment(false);
        }
      };
      checkPayment();
    }
  }, [sessionId, status]);

  const handleSubmit = async () => {
    try {
      await axios.post(`${API}/discovery-answers`, {
        lead_id: "", headache, pos_system: pos, masterclass_interest: masterclass,
      });
      setSubmitted(true);
      toast.success("Thank you for sharing!");
    } catch { toast.error("Failed to save. Try again."); }
  };

  // Show loading if checking payment
  if (checkingPayment) {
    return (
      <div className="min-h-screen bg-[#040F49] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-gold animate-spin mx-auto mb-4" />
          <p className="text-white text-lg">Confirming your payment...</p>
          <p className="text-slate-400 text-sm">This will only take a moment.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#040F49] relative">
      <div className="noise-overlay" />
      <div className="max-w-3xl mx-auto px-6 py-24">
        <motion.div className="text-center mb-12" initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}>
          <ShieldLogo size="lg" />
          <h1 className="font-inter text-4xl md:text-5xl font-black text-white mt-8" data-testid="thankyou-headline">
            Welcome to the <span className="text-gold">Inner Circle.</span>
          </h1>
          <p className="text-slate-400 mt-4 text-lg">
            {paymentConfirmed 
              ? "Payment confirmed! You're officially a Founding Member." 
              : `You're in, ${decodeURIComponent(memberName)}.`}
          </p>
        </motion.div>

        {/* Certificate */}
        <motion.div className="glass-card rounded-xl p-8 md:p-12 text-center mb-12 border border-gold/20 gold-glow"
          initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.3 }}>
          <Award className="w-12 h-12 text-gold mx-auto mb-4" />
          <p className="text-xs font-inter font-bold tracking-[0.3em] uppercase text-gold mb-2">Founding Member Certificate</p>
          <div className="inline-flex items-center justify-center w-24 h-24 rounded-full border-2 border-gold/40 mb-4">
            <span data-testid="member-number" className="text-3xl font-inter font-black text-gold">#{memberNum || "∞"}</span>
          </div>
          <p className="text-white font-inter font-bold text-lg">{decodeURIComponent(memberName)}</p>
          <p className="text-xs text-slate-500 mt-2">Founding Partner &middot; Argus Xenia</p>
          {paymentConfirmed && (
            <div className="mt-4 px-4 py-2 bg-lime/10 rounded-lg inline-flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-lime" />
              <span className="text-lime text-sm font-bold">Payment Confirmed</span>
            </div>
          )}
          <div className="flex justify-center gap-6 mt-6">
            <div className="text-center"><p className="text-gold font-bold text-lg">25%</p><p className="text-xs text-slate-500">Lifetime Discount</p></div>
            <div className="text-center"><p className="text-gold font-bold text-lg">VIP</p><p className="text-xs text-slate-500">Priority Setup</p></div>
            <div className="text-center"><p className="text-gold font-bold text-lg">Direct</p><p className="text-xs text-slate-500">Founder Access</p></div>
          </div>
        </motion.div>

        {/* Discovery questions */}
        {!submitted ? (
          <motion.div className="glass-card rounded-xl p-8 border border-gold/10"
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}>
            <h3 className="font-inter text-xl font-bold text-white mb-2">Help Us Help You</h3>
            <p className="text-sm text-slate-400 mb-6">Quick discovery questions so we can customize your Argus experience.</p>
            <div className="space-y-5">
              <div>
                <Label className="text-slate-300 text-sm">What is your #1 operational headache right now?</Label>
                <Textarea data-testid="discovery-headache" value={headache} onChange={e => setHeadache(e.target.value)}
                  placeholder="e.g. Staff turnover, food waste, scheduling nightmares..."
                  className="mt-2 bg-indigo-700/50 border-gold/20 text-white placeholder:text-slate-500 focus:border-gold h-20" />
              </div>
              <div>
                <Label className="text-slate-300 text-sm">What POS system do you use?</Label>
                <Input data-testid="discovery-pos" value={pos} onChange={e => setPos(e.target.value)} placeholder="e.g. Toast, Square, Clover..."
                  className="mt-2 bg-indigo-700/50 border-gold/20 text-white placeholder:text-slate-500 focus:border-gold" />
              </div>
              <div>
                <Label className="text-slate-300 text-sm">Interested in Voice-to-SOP training feature?</Label>
                <Input data-testid="discovery-masterclass" value={masterclass} onChange={e => setMasterclass(e.target.value)}
                  placeholder="Very interested / Somewhat / Not sure"
                  className="mt-2 bg-indigo-700/50 border-gold/20 text-white placeholder:text-slate-500 focus:border-gold" />
              </div>
              <Button data-testid="discovery-submit" onClick={handleSubmit}
                className="w-full bg-gold hover:bg-gold-500 text-indigo-700 font-inter font-bold rounded-none py-5">
                Submit <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </div>
          </motion.div>
        ) : (
          <motion.div className="glass-card rounded-xl p-8 text-center border border-lime/20"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <CheckCircle className="w-10 h-10 text-lime mx-auto mb-4" />
            <h3 className="font-inter text-lg font-bold text-white mb-2">Responses Saved!</h3>
            <p className="text-sm text-slate-400">We'll reach out within 24 hours to schedule your 15-minute founder briefing.</p>
          </motion.div>
        )}

        <div className="text-center mt-12">
          <a href="/" className="text-sm text-gold hover:text-gold-300 font-inter">&larr; Back to Argus Xenia</a>
        </div>
      </div>

      <ChatWidget
        agentType="founder"
        title="Talk to Argus Founder"
        context={{
          name: decodeURIComponent(memberName),
          member_number: parseInt(memberNum) || 0,
          email: memberEmail ? decodeURIComponent(memberEmail) : undefined,
        }}
        autoOpen={true}
        autoOpenDelay={3000}
      />
    </div>
  );
}
